import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import {
  Card,
  Box,
  Typography,
  Stack,
  Avatar,
  Divider,
} from "@mui/material";
import {
  AutoAwesomeOutlined as MRLIcon,
  ScheduleOutlined as ClockIcon,
  ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon,
  LocalFireDepartment as FireIcon,
  InfoOutlined as InfoIcon,
  ConfirmationNumberOutlined as TicketIcon,
} from "@mui/icons-material";
import moment from "moment";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";
import { DEFAULT_CURRENCY, Path, LOTTERY_TYPES, LOTTERY_NAMES } from "../../../../../static/Constants";
import { safeFromWei } from "../../../../../static/utils";
import getImageUrlByHash from "../../../../../static/logoUtils";
import { CommonButton } from "../../../../Components/ui";
import MegaMatrixPreview from "./MegaMatrixPreview";

const MegaLotteryCard = ({
  lottery,
  partner,
  partnerLogo,
  currency = DEFAULT_CURRENCY,
}) => {
  const history = useHistory();
  const typeConfig = LOTTERY_TYPES[2];

  const lotteryId = lottery?.lotteryId;
  const statusId = lottery?.status;
  const isRollover =
    statusId === "3" ||
    statusId === "5" ||
    Boolean(lottery?.rolloverperct && Number(lottery.rolloverperct) > 0);

  const totalPrizeFormatted = useMemo(
    () => safeFromWei(lottery?.totalPrize),
    [lottery?.totalPrize]
  );

  const entryFeeFormatted = useMemo(
    () => safeFromWei(lottery?.entryFee),
    [lottery?.entryFee]
  );

  const capacity = Number(lottery?.capacity) || 20;
  const pickNumbers = Number(lottery?.pickNumbers) || 4;

  const deployedDateFormatted = useMemo(() => {
    if (!lottery?.deployedOn) return null;
    return moment.unix(Number(lottery.deployedOn)).format("DD MMM YYYY");
  }, [lottery?.deployedOn]);

  const partnerLogoUrl = useMemo(() => {
    if (!partner?.logoHash) return null;
    return getImageUrlByHash(partner.logoHash, partnerLogo);
  }, [partner?.logoHash, partnerLogo]);

  const partnerDisplayName = useMemo(() => {
    if (partner?.name && partner.name.trim()) return partner.name;
    if (partner?.companyName && partner.companyName.trim()) return partner.companyName;
    const addr = lottery?.partnerAddress || lottery?.partner || "";
    if (addr && addr.length > 10) {
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
    }
    return "Verified Creator";
  }, [partner, lottery]);

  const handlePlayNow = () => {
    history.push(`/lottery/${lotteryId}`);
  };

  const handleViewDetails = () => {
    history.push(Path.getLotteryDetails(lotteryId));
  };

  return (
    <Card
      className="mega-lottery-card"
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: colors.dashboard.cardBg,
        borderRadius: borderRadiusPx.xl,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        transition: "all 0.25s ease",
        "&:hover": {
          borderColor: typeConfig.hoverBorder || typeConfig.color,
          boxShadow: typeConfig.boxShadow || "0 12px 28px rgba(0, 0, 0, 0.12)",
          transform: "translateY(-4px)",
        },
      }}
    >
      {/* Top Accent Gradient Bar */}
      <Box
        sx={{
          height: 4,
          background: typeConfig.gradient,
        }}
      />

      <Box sx={{ p: 2.75, flexGrow: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Badges Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ mb: 2 }}
        >
          {/* Game Type Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: borderRadiusPx.md,
                bgcolor: typeConfig.bgColor,
                color: typeConfig.color,
                border: `1px solid ${typeConfig.borderColor || `${typeConfig.color}30`}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MRLIcon sx={{ fontSize: 18 }} />
            </Box>
            <div>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: typeConfig.darkColor || typeConfig.color,
                  lineHeight: 1.1,
                  fontSize: "0.85rem",
                }}
              >
                {`${typeConfig.name} #${lotteryId}`}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: colors.slate[400], fontSize: "0.72rem" }}
              >
                Multi-Random
              </Typography>
            </div>
          </Stack>

          {/* Status Badge */}
          <Stack direction="row" spacing={1} alignItems="center">
            {isRollover && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1,
                  py: 0.25,
                  borderRadius: borderRadiusPx.full,
                  bgcolor: colors.amber[50],
                  border: `1px solid ${colors.amber[200]}`,
                  color: colors.amber[700],
                  fontSize: "0.72rem",
                  fontWeight: 700,
                }}
              >
                <FireIcon sx={{ fontSize: 13, color: colors.amber[600] }} />
                <span>Rollover</span>
              </Box>
            )}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.25,
                borderRadius: borderRadiusPx.full,
                bgcolor: typeConfig.bgColor,
                border: `1px solid ${typeConfig.borderColor || `${typeConfig.color}30`}`,
                color: typeConfig.darkColor || typeConfig.color,
                fontSize: "0.72rem",
                fontWeight: 700,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: typeConfig.color,
                }}
              />
              <span>Live</span>
            </Box>
          </Stack>
        </Stack>

        {/* Prize Pool Display */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: typeConfig.bgColor,
            border: `1px solid ${typeConfig.borderColor || `${typeConfig.color}30`}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <div>
              <Typography
                variant="caption"
                sx={{
                  color: typeConfig.darkColor || typeConfig.color,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  fontSize: "0.72rem",
                }}
              >
                Estimated Prize Pool
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: typeConfig.darkColor || typeConfig.color,
                  lineHeight: 1.2,
                }}
              >
                {totalPrizeFormatted}{" "}
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{ color: typeConfig.color, fontWeight: 700 }}
                >
                  {currency}
                </Typography>
              </Typography>
            </div>

            <Box
              sx={{
                p: 1,
                bgcolor: colors.neutral.white,
                borderRadius: borderRadiusPx.md,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <TicketIcon sx={{ color: typeConfig.color, fontSize: 22 }} />
            </Box>
          </Stack>
        </Box>

        {/* Mini Matrix Preview Component */}
        <MegaMatrixPreview capacity={capacity} pickNumbers={pickNumbers} />

        {/* Game Parameters Specs */}
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: borderRadiusPx.lg,
            bgcolor: colors.slate[50],
            border: `1px solid ${colors.slate[200]}`,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.slate[500], fontWeight: 500 }}
            >
              Entry Price:
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.slate[900], fontWeight: 700 }}
            >
              {entryFeeFormatted} {currency}
            </Typography>
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.slate[500], fontWeight: 500 }}
            >
              Selection Rule:
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: typeConfig.color, fontWeight: 700 }}
            >
              Pick {pickNumbers} of {capacity} Numbers
            </Typography>
          </Stack>

          {deployedDateFormatted && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="caption"
                sx={{ color: colors.slate[500], fontWeight: 500 }}
              >
                Deployed:
              </Typography>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ClockIcon sx={{ fontSize: 13, color: colors.slate[400] }} />
                <Typography
                  variant="caption"
                  sx={{ color: colors.slate[600], fontWeight: 600 }}
                >
                  {deployedDateFormatted}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Box>

        {/* Partner Creator Info */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: "auto", pt: 1.5, mb: 2 }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {partnerLogoUrl ? (
              <Avatar
                src={partnerLogoUrl}
                alt={partnerDisplayName}
                sx={{
                  width: 26,
                  height: 26,
                  border: `1px solid ${colors.slate[200]}`,
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 26,
                  height: 26,
                  bgcolor: colors.slate[200],
                  color: colors.slate[600],
                  fontSize: "0.75rem",
                  fontWeight: 700,
                }}
              >
                {partnerDisplayName.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[700],
                fontWeight: 600,
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {partnerDisplayName}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center">
            <VerifiedIcon sx={{ fontSize: 14, color: colors.blue[500] }} />
            <Typography
              variant="caption"
              sx={{ color: colors.blue[600], fontWeight: 600, fontSize: "0.72rem" }}
            >
              Verified Creator
            </Typography>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2, borderColor: colors.slate[100] }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={1.25}>
          <CommonButton
            variant="outline"
            size="small"
            onClick={handleViewDetails}
            startIcon={<InfoIcon sx={{ fontSize: 16 }} />}
            sx={{ flex: 1 }}
          >
            Details
          </CommonButton>

          <CommonButton
            variant="playNow"
            size="small"
            onClick={handlePlayNow}
            endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
            sx={{
              flex: 1.5,
              background: typeConfig.gradient,
              boxShadow: typeConfig.boxShadow,
              "&:hover": {
                background: typeConfig.gradient,
                filter: "brightness(1.08)",
              },
            }}
          >
            {`Play ${LOTTERY_NAMES.MEGABET}`}
          </CommonButton>
        </Stack>
      </Box>
    </Card>
  );
};

MegaLotteryCard.propTypes = {
  lottery: PropTypes.object.isRequired,
  partner: PropTypes.object,
  partnerLogo: PropTypes.array,
  currency: PropTypes.string,
};

export default MegaLotteryCard;
